import { currencySymbol, roundConfig } from './round-config'
import { api, ApiError, changeNow, mutate } from './api'
import { applySavedBusinessRow, businessSettings, wordMessage } from './business-settings'
import { loadMe } from './me'
import { el, toast } from './ui'
import { inStoreApp } from './store-app'

// A US or Canadian round's own Twilio steps: a US local number, texting registration, US carriers (Step 2 round 3).
const US = (): boolean => roundConfig().country !== 'GB'

// Extra features > "Calls and texts through Roundly" (0059; Liam, 10 Oct 2026: "Every business should have a way of
// switching this on and clear steps to do this it wont come out of our personal account would it?"; plan
// docs/plans/CALLS-AND-MESSAGES-PLAN.md). Each business connects its OWN Twilio account and pays Twilio itself. Not
// connected: the steps, each with a link, then four boxes and "Check and connect" (Roundly checks the details with Twilio
// and sets the number up itself). Connected: the number, three switches, the forwarding code for the work phone, what it
// costs, and Disconnect.

export const PHONE_PATH = '/api/business/phone'
export interface PhoneRow {
  connected: boolean
  ready: boolean
  number?: string
  status?: 'connected' | 'failed'
  problem?: string | null
  calls_on?: boolean
  texts_on?: boolean
  whatsapp_on?: boolean
  calls_ready?: boolean
  iphone_ready?: boolean
  whatsapp_ready?: boolean
  whatsapp_first?: string | null
  whatsapp_nudge?: string | null
  forward_code?: string
  version?: number
}

const shownNumber = (e164: string | undefined): string => (e164 ?? '').replace(/^\+44(\d{4})(\d{6})$/, '0$1 $2').replace(/^\+1(\d{3})(\d{3})(\d{4})$/, '($1) $2-$3')

function toggle(on: boolean, label: string, disabled = false): HTMLButtonElement {
  const b = el('button', { type: 'button', role: 'switch', class: `mb-switch feature-switch${on ? ' mb-switch-on' : ''}`, 'aria-checked': on ? 'true' : 'false', 'aria-label': label },
    el('span', { class: 'mb-switch-knob' }, el('span', { class: 'feature-tick', 'aria-hidden': 'true' }, '✓'))) as HTMLButtonElement
  b.disabled = disabled
  return b
}

// Send all payment texts (0093; docs/plans/send-all-texts/PLAN.md, Server 4): before Roundly texts everyone waiting for a
// payment message or reminder, the owner says once that their customers gave them their number for messages about their
// visits and payments (kept with when and who, business_settings.texts_agreed_at). Off: the button never shows.
export const AGREEMENT_WORDS = 'My customers gave me their number for messages about their visits and payments.'
function agreement(note: HTMLElement): HTMLElement {
  const on = businessSettings().texts_agreed === true
  const sw = toggle(on, 'Send all payment texts')
  sw.addEventListener('click', async () => {
    sw.disabled = true
    try {
      const current = await api<{ version: number }>('/api/business')
      const ack = await mutate<Record<string, unknown>>({ label: on ? 'Send all payment texts: off' : 'Send all payment texts: on', method: 'PATCH', path: '/api/business', body: { version: current.version, texts_agreed: !on } })
      if (ack.ok) { applySavedBusinessRow(ack.row); toast(on ? 'Send all payment texts is off.' : 'Send all payment texts is on: the button is on Owed, Day done and Finish my day.'); sw.replaceWith(agreement(note).querySelector('button')!); return }
      note.textContent = ack.error ?? "That didn't change. Try again."
    } catch (err) { note.textContent = (err as Error).message }
    sw.disabled = false
  })
  return el('div', { class: 'phone-switch-row phone-agree' }, el('div', {}, el('strong', {}, 'Send all payment texts'),
    el('p', { class: 'muted' }, 'One button on Owed, Day done and Finish my day texts everyone waiting for their payment message or next reminder, from your Roundly number. Switching it on says:'),
    el('p', { class: 'phone-agree-words' }, `"${AGREEMENT_WORDS}"`)), sw)
}

function phoneIcon(): HTMLElement {
  const box = el('span', { class: 'feature-icon', 'aria-hidden': 'true' })
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('width', '24'); svg.setAttribute('height', '24')
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  path.setAttribute('d', 'M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2')
  path.setAttribute('fill', 'none'); path.setAttribute('stroke', 'currentColor'); path.setAttribute('stroke-width', '2'); path.setAttribute('stroke-linejoin', 'round')
  svg.append(path)
  box.append(svg)
  return box
}

function link(href: string, words: string): HTMLAnchorElement {
  return el('a', { href, target: '_blank', rel: 'noopener' }, words) as HTMLAnchorElement
}

function field(label: string, input: HTMLInputElement, help: string): HTMLElement {
  input.id ||= `phone-field-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`
  return el('label', { class: 'booking-field', for: input.id }, label, input, el('span', { class: 'field-help' }, help))
}

// metaWhatsApp: the round's WhatsApp is connected through Meta (0081, src/whatsapp-card.ts): a business uses one WhatsApp,
// so this card leaves WhatsApp out (docs/plans/whatsapp-direct/PLAN.md item 7).
export function phoneCard(row: PhoneRow, reload: () => void, opts: { metaWhatsApp?: boolean } = {}): HTMLElement {
  const on = !!row.connected && row.status === 'connected' && !!(row.calls_on || row.texts_on || row.whatsapp_on)
  const card = el('section', { class: `feature-card${on ? ' feature-card-on' : ''}`, 'aria-labelledby': 'feature-phone' })
  card.append(el('div', { class: 'feature-top' },
    phoneIcon(),
    el('div', { class: 'feature-titles' }, el('h3', { id: 'feature-phone' }, 'Calls and texts through Roundly'),
      el('p', {}, inStoreApp() ? "Customers' calls ring Roundly on your phone and computer, and your texts go from Roundly with their replies coming back. It uses your own Twilio account." : "Customers' calls ring Roundly on your phone and computer, and your texts go from Roundly with their replies coming back. It uses your own Twilio account: you pay Twilio, never Roundly.")),
    el('div', { class: 'feature-control' }, el('span', { class: 'feature-status' }, !row.connected ? 'Not connected' : row.status === 'failed' ? 'Needs attention' : on ? 'On' : 'Connected'))))
  const note = el('p', { class: 'form-error', role: 'alert' })
  // Inside the iPhone or Android app: set up on a computer, with no prices or card talk in the app (Liam, 4 Nov 2026).
  if (!row.connected && inStoreApp()) card.append(el('div', { class: 'feature-preview' }, el('p', { class: 'feature-see' }, el('strong', {}, 'Set this up on a computer')),
    el('p', {}, 'Go to app.roundly.work on a computer, then More, Extra features.')))
  else if (!row.connected) connectSteps(card, row, note, reload)
  else connected(card, row, note, reload, opts)
  card.append(el('div', { class: 'feature-where' }, el('strong', {}, 'Where it appears'),
    el('span', {}, 'Every Call button rings from Roundly, missed calls show on Today, and New jobs sends texts from your Roundly number with replies landing on the enquiry.')))
  card.append(note)
  return card
}

function connectSteps(card: HTMLElement, row: PhoneRow, note: HTMLElement, reload: () => void): void {
  const steps = el('ol', { class: 'quotes-steps phone-steps' },
    el('li', {}, 'Make a Twilio account and add your card (', link('https://www.twilio.com/try-twilio', 'twilio.com'), `). Turn off auto top-up and keep a small balance, say ${currencySymbol()}20, so nothing can ever run up a bill.`),
    ...(US() ? [el('li', {}, 'Buy a US local number in Twilio: Phone Numbers, Buy a number, United States, Local, with Voice and SMS (', link('https://console.twilio.com/us1/develop/phone-numbers/manage/search', 'open it'), '). To send texts, Twilio asks you to register your business for texting first (A2P 10DLC): a few dollars, and about one to three weeks. Calls and WhatsApp work meanwhile.')] : [
      el('li', {}, 'Buy a UK mobile number in Twilio: Phone Numbers, Buy a number, United Kingdom, Mobile (', link('https://console.twilio.com/us1/develop/phone-numbers/manage/search', 'open it'), '). Twilio asks for proof of your address first; that can take a day.')]),
    el('li', {}, 'Make an API key: Account, API keys and tokens, Create API key, Standard (', link('https://console.twilio.com/us1/account/keys-credentials/api-keys', 'open it'), '). Copy its SID (starts SK) and its secret, which Twilio shows only once.'),
    el('li', {}, 'Copy your Account SID (starts AC) from the Twilio console home page.'),
    el('li', {}, 'Paste them below and tap Check and connect. Roundly checks them with Twilio and sets your number up itself.'))
  card.append(el('div', { class: 'feature-preview' }, el('p', { class: 'feature-see' }, el('strong', {}, 'To set it up:')), steps))
  if (!row.ready) {
    card.append(el('p', { class: 'muted' }, 'Roundly is not ready to connect phones yet. The steps above can be done now.'))
    return
  }
  const account = el('input', { type: 'text', autocomplete: 'off', spellcheck: 'false', placeholder: 'AC…' }) as HTMLInputElement
  const keySid = el('input', { type: 'text', autocomplete: 'off', spellcheck: 'false', placeholder: 'SK…' }) as HTMLInputElement
  const keySecret = el('input', { type: 'password', autocomplete: 'off', spellcheck: 'false' }) as HTMLInputElement
  const number = el('input', { type: 'tel', autocomplete: 'off', placeholder: US() ? '(512) 555-0142' : '07…' }) as HTMLInputElement
  const go = el('button', { type: 'button', class: 'btn-primary' }, 'Check and connect') as HTMLButtonElement
  const form = el('div', { class: 'booking-fields phone-connect' },
    field('Account SID', account, 'Step 4: starts AC.'),
    field('API key SID', keySid, 'Step 3: starts SK.'),
    field('API key secret', keySecret, 'Step 3: shown once by Twilio.'),
    field('Your Twilio number', number, US() ? 'Step 2: the local number you bought.' : 'Step 2: the UK mobile number you bought.'),
    go)
  card.append(form)
  const key = crypto.randomUUID()
  go.addEventListener('click', () => {
    note.textContent = ''
    go.disabled = true
    go.textContent = 'Checking with Twilio…'
    void changeNow<{ row: PhoneRow }>('POST', PHONE_PATH, { account_sid: account.value, api_key_sid: keySid.value, api_key_secret: keySecret.value, number: number.value }, key)
      .then(() => { toast('Connected. Now choose what to switch on.'); void loadMe(); reload() })
      .catch((err: Error) => {
        go.disabled = false
        go.textContent = 'Check and connect'
        const step = err instanceof ApiError && typeof (err.body as { step?: unknown } | null)?.step === 'number' ? (err.body as { step: number }).step : null
        note.textContent = `${step ? `Step ${step}: ` : ''}${err.message || "That didn't connect. Try again."}`
      })
  })
}

function connected(card: HTMLElement, row: PhoneRow, note: HTMLElement, reload: () => void, opts: { metaWhatsApp?: boolean } = {}): void {
  const box = el('div', { class: 'feature-preview' })
  box.append(el('div', { class: 'quotes-address' }, el('h4', {}, 'Your Roundly number'), el('p', { class: 'quotes-address-value' }, shownNumber(row.number))))
  if (row.status === 'failed') box.append(el('p', { class: 'form-error' }, `Needs attention: ${row.problem ?? 'Twilio stopped answering.'} Calls and texts through Roundly are paused until it is fixed.`))
  const rows: [keyof PhoneRow, string, string, boolean][] = [
    ['calls_on', 'Take calls in Roundly', "Calls to your Roundly number ring Roundly on your phone and computer; the Call buttons ring from it too.", !!row.calls_ready],
    ['texts_on', 'Texts from Roundly', 'Texts go from your Roundly number, and their replies land on the enquiry.', true],
    ['whatsapp_on', 'WhatsApp from Roundly', row.whatsapp_ready ? 'WhatsApp goes from your Roundly number.' : 'Waiting for WhatsApp to approve your number: see the steps below.', !!row.whatsapp_ready],
  ]
  if (opts.metaWhatsApp) rows.pop()
  const list = el('div', { class: 'phone-switches' })
  for (const [flag, title, help, ready] of rows) {
    const value = row[flag] === true
    const sw = toggle(value, title, !ready || row.status === 'failed')
    sw.addEventListener('click', () => {
      sw.disabled = true
      void mutate<{ row: PhoneRow }>({ label: `${value ? 'Turn off' : 'Turn on'} ${title.toLowerCase()}`, method: 'PATCH', path: PHONE_PATH, body: { [flag]: !value, version: row.version } })
        .then((ack) => {
          if (ack.ok) { void loadMe(); reload(); return }
          sw.disabled = false
          note.textContent = ack.error ?? "That didn't change. Try again."
        })
    })
    list.append(el('div', { class: 'phone-switch-row' }, el('div', {}, el('strong', {}, title), el('p', { class: 'muted' }, help)), sw))
  }
  box.append(list)
  if (row.texts_on && row.status !== 'failed') box.append(agreement(note))
  box.append(el('ul', { class: 'quotes-steps' },
    el('li', {}, 'On your work phone, dial ', el('strong', {}, row.forward_code ?? ''), US() ? ' once: calls you don\'t answer within 15 seconds then come to Roundly (AT&T and T-Mobile; on Verizon dial *71 and your Roundly number; ask your carrier if it says no).' : ' once: calls you don\'t answer within 15 seconds then come to Roundly (most UK networks; ask yours if it says no).'),
    ...(row.iphone_ready ? [] : [el('li', {}, "On the Roundly iPhone app a call shows as missed rather than ringing, until Roundly's Apple certificate is added. Computers ring now.")]),
    ...(opts.metaWhatsApp ? [] : [el('li', {}, 'WhatsApp: in Twilio, Messaging, Senders, WhatsApp senders, sign up with your Facebook business and this number. Meta checks your business first. Once it is approved, the WhatsApp switch above opens.')]),
    ...(inStoreApp() ? [] : [el('li', {}, US() ? 'You pay Twilio from your own account: the number about $1.15 a month, a text about 1¢ plus the carriers\' fees, a call about 1¢ a minute, WhatsApp a few cents a conversation.' : 'You pay Twilio from your own account: the number about £2 a month, a text about 3p, an incoming call about 1p a minute, WhatsApp a few pence a conversation.')])))
  if (!opts.metaWhatsApp) box.append(whatsappPart(row, note, reload))
  const off = el('button', { type: 'button', class: 'link-button' }, 'Disconnect Twilio') as HTMLButtonElement
  off.addEventListener('click', () => {
    if (!window.confirm('Disconnect Twilio? Calls and texts through Roundly stop at once, and your number stops sending to Roundly. Your Twilio account and number stay yours.')) return
    off.disabled = true
    // Roundly could not reach the number (the key was deleted in Twilio): the owner clears it in Twilio, or calls and
    // texts to it keep going nowhere.
    void changeNow<{ cleared?: boolean }>('DELETE', PHONE_PATH, {})
      .then((r) => {
        toast(r?.cleared === false
          ? "Disconnected, but Roundly couldn't reach your number. In Twilio, open your number and clear where its calls and texts go."
          : 'Disconnected. Your Twilio account and number are unchanged.')
        void loadMe(); reload()
      })
      .catch((err: Error) => { off.disabled = false; note.textContent = err.message })
  })
  box.append(off)
  card.append(box)
}

// WhatsApp (stage 4): Check WhatsApp reads whether the number is approved; once it is, the business's own first reply and
// nudge go to WhatsApp for approval, and what WhatsApp said shows here. Until approved, WhatsApp goes from the phone.
const said = (status: string | null | undefined): string =>
  !status ? 'not sent yet' : status === 'approved' ? 'approved' : status.startsWith('rejected') ? `refused (${status.replace(/^rejected:?\s*/, '') || 'no reason given'})` : 'waiting for WhatsApp'
function whatsappPart(row: PhoneRow, note: HTMLElement, reload: () => void): HTMLElement {
  const part = el('div', { class: 'phone-whatsapp' }, el('h4', {}, 'WhatsApp'))
  const run = (body: Record<string, unknown>, button: HTMLButtonElement, done: string): void => {
    button.disabled = true
    note.textContent = ''
    void changeNow('POST', '/api/business/whatsapp', body)
      .then(() => { toast(done); reload() })
      .catch((err: Error) => { button.disabled = false; note.textContent = err.message })
  }
  const check = el('button', { type: 'button', class: 'btn-secondary' }, 'Check WhatsApp') as HTMLButtonElement
  check.addEventListener('click', () => run({ action: 'check' }, check, 'Checked with WhatsApp.'))
  if (!row.whatsapp_ready) {
    part.append(el('p', { class: 'muted' }, 'Not approved for your number yet. Once Twilio shows your WhatsApp sender as online, tap Check WhatsApp.'), check)
    return part
  }
  part.append(el('p', {}, `First reply: ${said(row.whatsapp_first)}. Nudge: ${said(row.whatsapp_nudge)}.`))
  part.append(el('p', { class: 'muted' }, "WhatsApp only lets a business start a conversation with wording it has approved. Within a day of a customer's own WhatsApp, anything can be sent."))
  const send = el('button', { type: 'button', class: 'btn-primary' }, row.whatsapp_first ? 'Send my wording again' : 'Ask WhatsApp to approve my wording') as HTMLButtonElement
  send.addEventListener('click', () => {
    // The business's own wording (Your messages), the first name left for WhatsApp to fill in and the sign-off written in
    // (WhatsApp refuses wording that ends with a filled-in part).
    const settings = businessSettings()
    const fill = { 'first name': '{{1}}', 'your name': settings.booking_signoff }
    run({ action: 'wording', first: wordMessage('enquiry_first', fill, settings), nudge: wordMessage('quote_nudge', fill, settings) }, send, 'Sent to WhatsApp for approval. It usually answers within a day.')
  })
  part.append(send, check)
  return part
}
